import { supabase } from "@/integrations/supabase/client";
import { logActivity, ActivityAction, EntityType } from "./activity-logger";

/**
 * Automatically log database changes using Supabase hooks
 * This wrapper intercepts common database operations and logs them
 */

interface AutoLogOptions {
  action: ActivityAction;
  entityType: EntityType;
  entityId?: string | number;
  details?: Record<string, unknown>;
}


/**
 * Wrap a database operation with automatic activity logging
 */
export async function withActivityLog<T>(
  operation: () => Promise<T>,
  logOptions: AutoLogOptions
): Promise<T> {
  try {
    const result = await operation();
    
    // Log the activity asynchronously (don't wait)
    logActivity({
      action: logOptions.action,
      entityType: logOptions.entityType,
      entityId: logOptions.entityId?.toString(),
      details: logOptions.details,
    }).catch(error => {
      console.error('Failed to log activity:', error);
    });
    
    return result;
  } catch (error) {
    // Still try to log failed operations
    logActivity({
      action: logOptions.action,
      entityType: logOptions.entityType,
      entityId: logOptions.entityId?.toString(),
      details: {
        ...logOptions.details,
        error: error instanceof Error ? error.message : 'Unknown error',
        success: false
      },
    }).catch(logError => {
      console.error('Failed to log activity:', logError);
    });
    
    throw error;
  }
}

/**
 * Supabase client wrapper with automatic activity logging
 */
export const loggedSupabase = {
  /**
   * Insert with logging
   */
  insert: async (
    table: string,
    data: Record<string, unknown> | Record<string, unknown>[],
    entityType: EntityType
  ): Promise<unknown> => {
    return withActivityLog(
      async () => {
        const { data: result, error } = await (supabase as any)
          .from(table)
          .insert(data)
          .select();
        
        if (error) throw error;
        return result;
      },
      {
        action: 'create',
        entityType,
        details: { count: Array.isArray(data) ? data.length : 1 }
      }
    );
  },

  /**
   * Update with logging
   */
  update: async (
    table: string,
    id: string | number,
    data: Record<string, unknown>,
    entityType: EntityType
  ): Promise<unknown> => {
    return withActivityLog(
      async () => {
        const { data: result, error } = await (supabase as any)
          .from(table)
          .update(data)
          .eq('id', id)
          .select();
        
        if (error) throw error;
        return result;
      },
      {
        action: 'update',
        entityType,
        entityId: id,
        details: { fields: Object.keys(data) }
      }
    );
  },

  /**
   * Delete with logging
   */
  delete: async (
    table: string,
    id: string | number,
    entityType: EntityType
  ): Promise<boolean> => {
    return withActivityLog(
      async () => {
        const { error } = await (supabase as any)
          .from(table)
          .delete()
          .eq('id', id);
        
        if (error) throw error;
        return true;
      },
      {
        action: 'delete',
        entityType,
        entityId: id
      }
    );
  },
};

/**
 * Log authentication events
 */
export async function logAuthEvent(action: 'login' | 'logout', email?: string): Promise<void> {
  await logActivity({
    action,
    entityType: 'settings', // Using settings as a general category
    details: { email }
  });
}
